import { supabase } from '../lib/supabase';

// ─── Helpers ────────────────────────────────────────────────────────────────

const toCamel = (row) => {
  if (!row) return null;
  return {
    id: row?.id,
    fullName: row?.full_name,
    email: row?.email,
    phone: row?.phone,
    whatsapp: row?.whatsapp,
    category: row?.category,
    specialty: row?.specialty,
    bio: row?.bio,
    yearsExperience: row?.years_experience,
    city: row?.city,
    commune: row?.commune,
    address: row?.address,
    serviceZones: row?.service_zones || [],
    status: row?.status,
    isVerified: row?.is_verified,
    isFeatured: row?.is_featured,
    avatarUrl: row?.avatar_url,
    portfolioImages: row?.portfolio_images || [],
    ratingAverage: row?.rating_average,
    ratingCount: row?.rating_count,
    completedJobs: row?.completed_jobs,
    hourlyRate: row?.hourly_rate,
    dailyRate: row?.daily_rate,
    currency: row?.currency,
    availableDays: row?.available_days || [],
    availableHoursStart: row?.available_hours_start,
    availableHoursEnd: row?.available_hours_end,
    responseTimeHours: row?.response_time_hours,
    websiteUrl: row?.website_url,
    linkedinUrl: row?.linkedin_url,
    tags: row?.tags || [],
    certifications: row?.certifications || [],
    languages: row?.languages || [],
    notes: row?.notes,
    createdBy: row?.created_by,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
};

const toSnake = (data) => ({
  full_name: data?.fullName,
  email: data?.email || null,
  phone: data?.phone || null,
  whatsapp: data?.whatsapp || null,
  category: data?.category,
  specialty: data?.specialty || null,
  bio: data?.bio || null,
  years_experience: data?.yearsExperience ? parseInt(data?.yearsExperience) : 0,
  city: data?.city || 'Abidjan',
  commune: data?.commune || null,
  address: data?.address || null,
  service_zones: data?.serviceZones || [],
  status: data?.status || 'pending',
  is_verified: data?.isVerified || false,
  is_featured: data?.isFeatured || false,
  avatar_url: data?.avatarUrl || null,
  portfolio_images: data?.portfolioImages || [],
  hourly_rate: data?.hourlyRate ? parseFloat(data?.hourlyRate) : null,
  daily_rate: data?.dailyRate ? parseFloat(data?.dailyRate) : null,
  currency: data?.currency || 'XOF',
  available_days: data?.availableDays || [],
  available_hours_start: data?.availableHoursStart || null,
  available_hours_end: data?.availableHoursEnd || null,
  response_time_hours: data?.responseTimeHours ? parseInt(data?.responseTimeHours) : 24,
  website_url: data?.websiteUrl || null,
  linkedin_url: data?.linkedinUrl || null,
  tags: data?.tags || [],
  certifications: data?.certifications || [],
  languages: data?.languages || ['Français'],
  notes: data?.notes || null,
});

// ─── CRUD ────────────────────────────────────────────────────────────────────

export const getProfessionals = async ({ category, status, search, page = 1, pageSize = 20 } = {}) => {
  try {
    let query = supabase?.from('professionals')?.select('*', { count: 'exact' })?.order('created_at', { ascending: false });

    if (category && category !== 'all') query = query?.eq('category', category);
    if (status && status !== 'all') query = query?.eq('status', status);
    if (search) {
      query = query?.or(`full_name.ilike.%${search}%,specialty.ilike.%${search}%,commune.ilike.%${search}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query?.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    return { data: (data || [])?.map(toCamel), count: count || 0, error: null };
  } catch (error) {
    console.error('getProfessionals error:', error?.message);
    return { data: [], count: 0, error: error?.message };
  }
};

/**
 * Public-facing query — returns ONLY approved professionals.
 * Used in search, booking, and any user-visible listing.
 */
export const getApprovedProfessionals = async ({ category, search, page = 1, pageSize = 50 } = {}) => {
  try {
    let query = supabase
      ?.from('professionals')
      ?.select('*', { count: 'exact' })
      ?.eq('status', 'active')
      ?.order('is_featured', { ascending: false })
      ?.order('rating_average', { ascending: false });

    if (category && category !== 'all') query = query?.eq('category', category);
    if (search) {
      query = query?.or(
        `full_name.ilike.%${search}%,specialty.ilike.%${search}%,commune.ilike.%${search}%,tags.cs.{${search}}`
      );
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query?.range(from, to);

    const { data, error, count } = await query;
    if (error) throw error;

    return { data: (data || [])?.map(toCamel), count: count || 0, error: null };
  } catch (error) {
    console.error('getApprovedProfessionals error:', error?.message);
    return { data: [], count: 0, error: error?.message };
  }
};

export const getProfessionalById = async (id) => {
  try {
    const { data, error } = await supabase?.from('professionals')?.select('*')?.eq('id', id)?.single();
    if (error) throw error;
    return { data: toCamel(data), error: null };
  } catch (error) {
    console.error('getProfessionalById error:', error?.message);
    return { data: null, error: error?.message };
  }
};

export const createProfessional = async (professionalData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    const payload = { ...toSnake(professionalData), created_by: user?.id || null };

    const { data, error } = await supabase?.from('professionals')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return { data: toCamel(data), error: null };
  } catch (error) {
    console.error('createProfessional error:', error?.message);
    return { data: null, error: error?.message };
  }
};

export const updateProfessional = async (id, professionalData) => {
  try {
    const payload = toSnake(professionalData);

    const { data, error } = await supabase?.from('professionals')?.update(payload)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return { data: toCamel(data), error: null };
  } catch (error) {
    console.error('updateProfessional error:', error?.message);
    return { data: null, error: error?.message };
  }
};

export const deleteProfessional = async (id) => {
  try {
    const { error } = await supabase?.from('professionals')?.delete()?.eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('deleteProfessional error:', error?.message);
    return { error: error?.message };
  }
};

export const toggleProfessionalStatus = async (id, status) => {
  try {
    const { data, error } = await supabase?.from('professionals')?.update({ status })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return { data: toCamel(data), error: null };
  } catch (error) {
    console.error('toggleProfessionalStatus error:', error?.message);
    return { data: null, error: error?.message };
  }
};

export const toggleVerification = async (id, isVerified) => {
  try {
    const { data, error } = await supabase?.from('professionals')?.update({ is_verified: isVerified })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return { data: toCamel(data), error: null };
  } catch (error) {
    console.error('toggleVerification error:', error?.message);
    return { data: null, error: error?.message };
  }
};

export const CATEGORIES = [
  { value: 'architecte', label: 'Architecte' },
  { value: 'geometre', label: 'Géomètre' },
  { value: 'decorateur', label: 'Décorateur' },
  { value: 'demenageur', label: 'Déménageur' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'electricien', label: 'Électricien' },
  { value: 'plombier', label: 'Plombier' },
  { value: 'menuisier', label: 'Menuisier' },
  { value: 'climatisation', label: 'Climatisation' },
  { value: 'securite', label: 'Sécurité' },
  { value: 'autre', label: 'Autre' },
];

export const STATUSES = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
  { value: 'pending', label: 'En attente' },
  { value: 'suspended', label: 'Suspendu' },
];

export const DAYS = [
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
  { value: 'dimanche', label: 'Dimanche' },
];
